'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useMemo } from 'react'

import { PLBaseWidgetProps, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetPagination } from '@src/components/pl-base-widget/hooks/use-widget-pagination'
import { createWidgetDataFetchingService } from '@src/components/pl-base-widget/utils/fetch-widget-data'
import { usePLPageMeta } from '@src/components/pl-page-context'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import {
  isShowSecondImageEnabledForSlug,
  useStatsigShowSecondImageOnProductTilesConfig,
} from '@src/experiments/statsig-integration/use-statsig-show-second-image-on-product-tiles-config.hook'
import { useSwipeableProductCardExperiment } from '@src/experiments/statsig-integration/use-swipeable-product-card-experiment.hook'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'
import SubCollectionSection from '@src/pages/[...slug]/components/collection/molecules/subCollectionSection'
import { LoaderWrapper } from '@src/quince-components/src'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'

import type {
  ProductListWidgetData,
  WidgetChild,
  ProductCardData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { WidgetFetchResponse } from '@lastbrand/storefront-proto-registry/api/v1/widgetfetch'

// eslint-disable-next-line i18next/no-literal-string -- Panda spacing tokens (not user-facing copy)
const containerClass = css({
  px: { lg: 'space-48', base: 'space-16' },
})

export function ProductGridDSWidget(props: PLTypedWidgetProps<ProductListWidgetData>) {
  const pageMeta = usePLPageMeta()
  // Use widgetId (human-readable like "widget-product-list-5") for API calls,
  // not id (UUID) - the API expects widgetId as the identifier
  const widgetId = props.widgetId
  const productViewTracker = pageMeta.productViewTracker ?? ''

  const initialSSRData = useMemo(() => {
    return {
      children: props.children,
      pagination: props.pagination,
    }
  }, [props.children, props.pagination])

  const paginationLimit = props.pagination?.limit
  const pagePathname = pageMeta.pathname
  const pageSlug = pageMeta.seoData?.slug
  const pageSearchParams = pageMeta.searchParams
  const pagePlatformType = pageMeta.platformType
  const pageLbUserId = pageMeta.lbUserId
  const pageZipCode = pageMeta.zipCode

  // TEMPORARY (STRFRNT-15810): ask PL for SVD-shaped cards on infinite-scroll pages too, matching the
  // SSR first page. See `SVD_ENRICH_VERSION_HEADER` doc for current environment status.
  const svdEnrichHeaderEnabled = useIsSvdEnabled()

  const dataFetchingService = useMemo(() => {
    return createWidgetDataFetchingService(props as PLBaseWidgetProps, pageMeta, svdEnrichHeaderEnabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationLimit,
    pagePathname,
    pageSlug,
    pageSearchParams,
    pagePlatformType,
    pageLbUserId,
    pageZipCode,
    svdEnrichHeaderEnabled,
  ])

  const {
    data: paginatedData,
    isLoading,
    hasMore,
    intersectRef,
  } = useWidgetPagination(widgetId, initialSSRData, dataFetchingService)

  const showSecondImageOnProductTilesConfig = useStatsigShowSecondImageOnProductTilesConfig()
  const showSecondImageOnProductTiles = isShowSecondImageEnabledForSlug(
    showSecondImageOnProductTilesConfig,
    pageMeta.pageMetaData?.slug ?? '',
  )
  const enableImageCarousel = useSwipeableProductCardExperiment()

  // Merge children from initial SSR data and all paginated pages
  const allChildren = useMemo(() => {
    const initialChildren = (paginatedData.children as WidgetChild[] | undefined) ?? []
    const paginatedPages = (paginatedData.paginatedPages as WidgetFetchResponse[] | undefined) ?? []

    const paginatedChildren = paginatedPages.flatMap(page => page.children)

    return [...initialChildren, ...paginatedChildren]
  }, [paginatedData])

  const productCards = useMemo(() => {
    return allChildren
      .map((child: WidgetChild) => {
        const productCardData: ProductCardData | undefined = child.data?.productItem
        if (!productCardData) {
          return null
        }

        return { product: productCardData } as unknown as IProductCard
      })
      .filter((card): card is IProductCard => card !== null)
  }, [allChildren])

  const paginatedWidget = paginatedData as unknown as PLBaseWidgetProps

  const paginationTotal = paginatedWidget.pagination?.total
  const totalProducts = paginationTotal ?? productCards.length

  const subCollectionData = useMemo(
    () =>
      new SubCollection({
        id: String(widgetId),
        subTitle: undefined,
        showAllColors: false,
        noIndex: false,
        productCards,
        marketingPanelBase: undefined,
        marketingPanelMobile: undefined,
        additionalAttributes: {},
        revSortedIdColor: [],
        totalProducts,
      }),
    [widgetId, productCards, totalProducts],
  )

  return (
    <div data-testid="product-grid-widget" className={containerClass}>
      <SubCollectionSection
        subCollectionData={subCollectionData}
        showBorderBottom={false}
        isFirstCollection={true}
        tracker={''}
        additionalAttributes={{}}
        doHolidayCutoffSorting={false}
        collectionGender={undefined}
        collectionSlug={pageMeta.pathname}
        productViewTracker={productViewTracker}
        showSecondImageOnProductTiles={showSecondImageOnProductTiles}
        enableImageCarousel={enableImageCarousel}
        showShimmerOnProductCards={false}
      />
      <div ref={intersectRef} style={{ height: '1px', width: '100%', visibility: 'hidden' }} />
      <LoaderWrapper
        testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
        showLoader={isLoading && hasMore && productCards.length > 0}
        // eslint-disable-next-line i18next/no-literal-string
        minHeight="40rem"
      >
        <div />
      </LoaderWrapper>
    </div>
  )
}
